import { ModalAction } from '../ModalAction'
import { useCoachingString } from '../useCoachingString'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { avgsForm } from '../../../templates'
import { updateFormField, initialFormValues, type FormValues } from '@/core/components/form/formModel'
import { FormRenderer, type TextInputRules } from '@/core/components/form/FormRenderer'
import { gateAdvance } from '@/core/lib/advanceGate'
import { useAutosave } from '@/core/hooks/useAutosave'
import { useApp } from '@/core/state/appStore'
import { avgsSelectRequiredFields, loadAvgs, saveAvgsDraft, checkAvgs, applyAvgsConfirm, getAvgsPath, reassignAvgsPage } from '../../../flows/avgs'
import { avgsLooksPlausible } from '../../../lib/avgsPlausible'
import type { AvgsForm } from '../../../lib/extractAvgs'
import { handleFailure } from '@/core/lib/toast'
import { downloadStaffDocument } from '../../../lib/downloadStaffDocument'
import { getJourneyPosition } from '@/core/data/coachings'
import { aufgabeForName } from '@/core/data/aufgabe'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { Button, Text, Icon, ScrollBar, toastError, toastSuccess } from 'admination-react'
import { PdfRegion } from '@/core/components/modal/PdfRegion'

const AVGS_INPUT_RULES: TextInputRules = new Map([
  ['Maßnahme.Zeitlicher Umfang (max UE)', { filter: /[0-9]/, inputMode: 'numeric' }],
  ['Teilnehmer.AVGS-Nummer (laufende Nummer)', { filter: /[0-9]/, inputMode: 'numeric' }],
  ['Teilnehmer.Kundennummer', { filter: /[A-Za-z0-9]/ }],
  ['Maßnahme.Gültigkeit Beginn', { filter: /[0-9.]/, maxLength: 10 }],
  ['Maßnahme.Gültigkeit Ende', { filter: /[0-9.]/, maxLength: 10 }],
])

export function AvgsDokument({ coachingId }: { coachingId: string | null }) {
  const { dispatch, reloadDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [view, setView] = useState<'edit' | 'doc'>('edit')
  const [values, setValues] = useState<FormValues | null>(null)
  const [advancing, setAdvancing] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const ready = values !== null

  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [pageInput, setPageInput] = useState('1')
  const [reassigning, setReassigning] = useState(false)

  const setField = useCallback((path: string, v: unknown) => {
    setValues((prev) => updateFormField(prev, path, v))
  }, [])

  const check = useMemo(() => (values ? checkAvgs(values) : null), [values])

  useEffect(() => {
    let cancelled = false
    setView('edit')
    ;(async () => {
      const saved = coachingId ? await loadAvgs(coachingId) : null
      const init = initialFormValues(avgsForm, saved)
      if (!cancelled) {
        setValues(init)
        setAttempted(false)
        setBannerDismissed(false)
        setPageInput('1')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const [path, setPath] = useCoachingString(coachingId, getAvgsPath)

  const save = useCallback(
    async (v: FormValues) => {
      if (!coachingId) return
      await saveAvgsDraft(coachingId, v as Record<string, unknown>)
    },
    [coachingId],
  )
  useAutosave(values, ready && !!coachingId, (v) => {
    if (v) return save(v)
  }, 250, { form: "AvgsDokument", record: coachingId ?? undefined, label: 'AVGS', recover: setValues })

  function confirmReassign() {
    if (!coachingId || reassigning) return
    const pageNumber = Number(pageInput)
    if (!Number.isInteger(pageNumber) || pageNumber < 1) return
    setReassigning(true)
    void (async () => {
      try {
        const result = await reassignAvgsPage(coachingId, pageNumber - 1)
        if (!result.ok) {
          handleFailure(result)
          return
        }
        const next = initialFormValues(avgsForm, result.data as unknown as Record<string, unknown>)
        setValues(next)
        setAttempted(false)
        if (avgsLooksPlausible(result.data)) setBannerDismissed(true)
        try {
          const p = await getAvgsPath(coachingId)
          setPath(p)
        } catch (e) {
          console.error('AVGS-Pfad nach Neuzuordnung fehlgeschlagen:', e)
        }
      } catch (e) {
        console.error('AVGS-Seite neu zuordnen fehlgeschlagen:', e)
        toastError('AVGS-Seite konnte nicht neu zugeordnet werden.')
      } finally {
        setReassigning(false)
      }
    })()
  }

  function advance() {
    setAttempted(true)
    gateAdvance(check, () => {
      void (async () => {
        if (advancing || !coachingId || !values) return
        setAdvancing(true)
        try {
          const r = await applyAvgsConfirm(coachingId, values as Record<string, unknown>)
          if (!r.ok) {
            handleFailure(r)
            return
          }
          toastSuccess('Der AVGS wurde erfolgreich eingelesen.')
          reloadDashboard()
          let tab = 'tb-hochladen'
          try {
            const { aufgabeName } = await getJourneyPosition(coachingId)
            const entry = aufgabeForName(aufgabeName)
            if (entry?.landing?.modal === 'avgs') tab = entry.landing.tab
          } catch (e) {
            console.error('Journey-Position (AVGS-Bestätigung) fehlgeschlagen:', e)
          }
          dispatch({ type: "CHANGE_TAB", tab })
        } finally {
          setAdvancing(false)
        }
      })()
    })
  }

  return (
    <>
      <ModalHeader size={view === 'doc' ? 'document' : 'form'} onClose={() => dispatch({ type: 'CLOSE' })}>
        <div className="pill-cluster">
          {view === 'edit' ? (
            <>
              <ModalAction
                icon="i-doc"
                title="Dokument"
                onClick={() => setView('doc')}
              />
              <ModalAction
                icon="i-arrow-right"
                primaryAction
                title="Weiter zur Trägerbestätigung"
                loading={advancing}
                disabled={!ready || advancing}
                onClick={advance}
              />
            </>
          ) : (
            <>
              <div className="pill">
                <Button type="button" iconOnly title="Herunterladen" onClick={() => downloadStaffDocument(path, coachingId, 'AVGS')}>
                  <Icon name="i-download" />
                </Button>
                <Button
                  type="button"
                  iconOnly
                  title="Korrigieren"
                  onClick={() => setView('edit')}
                >
                  <Icon name="i-edit" />
                </Button>
              </div>

            </>
          )}
        </div>
      </ModalHeader>
      {view === 'edit' ? (
        <>
          <main className="form" data-scroll-host ref={scrollerRef}>
            {values && !avgsLooksPlausible(values as unknown as AvgsForm) && !bannerDismissed && (
              <div role="alert" className="callout callout--row is-warning">
                <p className="callout-text">
                  Seite 1 sieht nicht wie ein AVGS-Gutschein aus. Falls die Seiten in anderer Reihenfolge lagen: auf
                  welcher Seite ist der AVGS?
                </p>
                <label className="callout-control">
                  AVGS ist auf Seite
                  <Text
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    className="text text--narrow"
                    aria-label="AVGS-Seitenzahl"
                  />
                </label>
                <div className="pill">
                  <Button primaryAction
                    type="button"
                    iconOnly
                    title="Seite bestätigen"
                    loading={reassigning}
                    disabled={reassigning || !Number.isInteger(Number(pageInput)) || Number(pageInput) < 1}
                    onClick={confirmReassign}
                  >
                    <Icon name="i-check" />
                  </Button>
                  <Button
                    type="button"
                    iconOnly
                    title="Hinweis schließen"
                    onClick={() => setBannerDismissed(true)}
                  >
                    <Icon name="i-close" />
                  </Button>
                </div>
              </div>
            )}
            {values && (
              <FormRenderer
                  definition={avgsForm}
                  values={values}
                  onChange={setField}
                  errors={check?.errors}
                  attemptedAdvance={attempted}
                  field={(field) => ({
                    required: field.control === 'select' && avgsSelectRequiredFields?.has(field.id),
                    ...AVGS_INPUT_RULES?.get(field.id),
                  })}
                />
            )}
          </main>
          <ScrollBar scrollerRef={scrollerRef} />
        </>
      ) : (
        <PdfRegion
          path={path ?? null}
          waiting={path === undefined}
          loadingLabel="AVGS wird geladen …"
          placeholder="Noch kein AVGS hochgeladen."
        />
      )}
    </>
  )
}
